import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox, Divider, Grid, lighten, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { useTranslation } from "react-i18next";
import DialogTitle from "src/containers/DialogTitle/DialogTitle";
import { UndefinedFieldText } from "src/containers/ManageIncomingMailsContainer/ManageIncomingMailsList/IncomingMailCard.tsx/styles";
import { DocumentFileDTO } from "src/models/documents/document-files";
import { customFileIconStyles } from "src/reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import DocumentViewer from "src/reusable-components/DocumentViewer/DocumentViewer";
import { getFileExtension } from "src/utils/files-utils/get-file-extension";
import SuggestedByAiHint from "src/views/manage-mail/SuggestedByAiHint";
import { Props } from "./interfaces";
import {
  AddresseeNicknameContainer,
  AddresseNickname,
  BlockDocumentAndDate,
  BlockDocuments,
  BlockEdit,
  BlockProcessTypeAndPostVia,
  BlockSelect,
  BlockStatuses,
  BlockTo,
  CardWrapper,
  DateContainer,
  DocumentViewerContainer,
  StatusContainer,
  StyledCard,
} from "./styles";
import { getItemPeriodValue } from "../../../../models/documents/period";
import { MAIL_CLASSIFICATION_STATUS, OUTGOING_MAIL_STATUSES } from "../../../../utils/constants/constants";
import { intlFormatDateShort, listOfMonths } from "../../../../utils/time-utils";
import { FileIconButton } from "../../../AttachedFiles/styles";
import OutgoingMailActions from "../OutgoingMailActions/OutgoingMailActions";

const OutgoingMailCard = ({ outgoingMail, handleNotifyParent, canUpload, canClassify, canRead, canProcess }: Props) => {
  const tRP = "components.outgoing_mail_card";
  const { t } = useTranslation();
  const theme = useTheme();

  const [fileToView, setFileToView] = useState<DocumentFileDTO | null>(null);

  return (
    <>
      <CardWrapper>
        <StyledCard>
          <BlockSelect>
            <Checkbox />
          </BlockSelect>

          <Divider
            orientation="vertical"
            style={{
              borderColor: lighten(theme.palette.secondary.light, 0.6),
              alignSelf: "center",
              height: "85%",
            }}
          />

          <BlockDocumentAndDate flex={1}>
            {outgoingMail.document?.files[0] && (
              <FileIconButton onClick={() => setFileToView(outgoingMail.document!.files[0])}>
                <FileIcon
                  extension={getFileExtension(outgoingMail.document.files[0].name)}
                  {...customFileIconStyles[
                    getFileExtension(outgoingMail.document.files[0].name) as DefaultExtensionType
                  ]}
                />
              </FileIconButton>
            )}
            <Typography>{outgoingMail.createdByUser?.name}</Typography>
            <DateContainer>
              <Typography variant="h5">{new Date(outgoingMail.createdAt).getDate()}</Typography>
              <Grid>
                <Typography variant="body2">{`${t(
                  listOfMonths[new Date(outgoingMail.createdAt).getMonth()]
                )}`}</Typography>
                <Typography variant="body2">{new Date(outgoingMail.createdAt).getFullYear()}</Typography>
              </Grid>
            </DateContainer>
          </BlockDocumentAndDate>

          <Divider
            orientation="vertical"
            style={{
              borderColor: lighten(theme.palette.secondary.light, 0.6),
              alignSelf: "center",
              height: "85%",
            }}
          />

          <BlockProcessTypeAndPostVia flex={2}>
            {!outgoingMail.toBeSigned && !outgoingMail.toBeStamped ? (
              <UndefinedFieldText>{`${t(`${tRP}.undefined_process_type`)}`}</UndefinedFieldText>
            ) : (
              <Typography variant="h5">
                {outgoingMail.toBeSigned && `${t(`${tRP}.sign`)}`} &nbsp;
                {outgoingMail.toBeSigned && outgoingMail.toBeStamped && `${t("common_words.and")}`} &nbsp;
                {outgoingMail.toBeStamped && `${t(`${tRP}.stamp`)}`}
              </Typography>
            )}
            {outgoingMail.postVia ? (
              <Typography>
                {`${t(`${tRP}.post_via`)}`} : {outgoingMail.postVia?.name}
              </Typography>
            ) : (
              <UndefinedFieldText>{`${t(`${tRP}.undefined_post_option`)}`}</UndefinedFieldText>
            )}
          </BlockProcessTypeAndPostVia>
          <BlockTo flex={2}>
            <FontAwesomeIcon
              icon={faArrowRight}
              color={theme.palette.primary.main}
            />
            {outgoingMail.addressedTo ? (
              <AddresseeNicknameContainer>
                <Typography
                  variant="h5"
                  color={theme.palette.primary.main}
                >
                  {outgoingMail.addressedTo.nickname}
                </Typography>
              </AddresseeNicknameContainer>
            ) : outgoingMail.addressedToOcr ? (
              <AddresseeNicknameContainer>
                <AddresseNickname variant="h5">{outgoingMail.addressedToOcr?.nickname}</AddresseNickname>
                <SuggestedByAiHint />
              </AddresseeNicknameContainer>
            ) : (
              <UndefinedFieldText>{`${t(`${tRP}.undefined_addressee`)}`}</UndefinedFieldText>
            )}
          </BlockTo>
          <BlockDocuments flex={1.5}>
            <Grid>
              {outgoingMail.processedDocument ? (
                <>
                  <Typography variant="body2">{`${t(`${tRP}.processed_document`)}`}</Typography>
                  {outgoingMail.processedDocument?.files[0] && (
                    <FileIconButton onClick={() => setFileToView(outgoingMail.processedDocument!.files[0])}>
                      <FileIcon
                        extension={getFileExtension(outgoingMail.processedDocument.files[0].name)}
                        {...customFileIconStyles[
                          getFileExtension(outgoingMail.processedDocument.files[0].name) as DefaultExtensionType
                        ]}
                      />
                    </FileIconButton>
                  )}
                </>
              ) : (
                <Typography variant="body2">
                  <i>{`${t(`${tRP}.no_processed_document`)}`}</i>
                </Typography>
              )}
            </Grid>
            <Grid>
              {outgoingMail.sendingDocument ? (
                <>
                  <Typography variant="body2">{`${t(`${tRP}.sending_document`)}`}</Typography>
                  {outgoingMail.sendingDocument?.files[0] && (
                    <FileIconButton onClick={() => setFileToView(outgoingMail.sendingDocument!.files[0])}>
                      <FileIcon
                        extension={getFileExtension(outgoingMail.sendingDocument.files[0].name)}
                        {...customFileIconStyles[
                          getFileExtension(outgoingMail.sendingDocument.files[0].name) as DefaultExtensionType
                        ]}
                      />
                    </FileIconButton>
                  )}
                </>
              ) : (
                <Typography variant="body2">
                  <i>{`${t(`${tRP}.no_sending_document`)}`}</i>
                </Typography>
              )}
            </Grid>
          </BlockDocuments>
          <BlockStatuses flex={1.5}>
            <StatusContainer
              flex={1}
              style={{
                backgroundColor:
                  outgoingMail.status?.id === OUTGOING_MAIL_STATUSES.Pending.id
                    ? lighten(theme.palette.primary.light, 0.5)
                    : outgoingMail.status?.id === OUTGOING_MAIL_STATUSES.Posted.id
                    ? lighten(theme.palette.success.light, 0.5)
                    : lighten(theme.palette.secondary.light, 0.5),
              }}
            >
              <Typography
                style={{
                  color:
                    outgoingMail.status?.id === OUTGOING_MAIL_STATUSES.Pending.id
                      ? theme.palette.primary.main
                      : outgoingMail.status?.id === OUTGOING_MAIL_STATUSES.Posted.id
                      ? theme.palette.success.main
                      : theme.palette.secondary.main,
                }}
              >
                <b>{`${t(`outgoing_mail_statuses.${outgoingMail.status?.name}`)}`}</b>
              </Typography>
            </StatusContainer>
            <StatusContainer
              flex={1}
              style={{
                backgroundColor:
                  outgoingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.ToBeClassified.id
                    ? lighten(theme.palette.primary.light, 0.5)
                    : outgoingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.Classified.id
                    ? lighten(theme.palette.success.main, 0.8)
                    : lighten(theme.palette.secondary.light, 0.5),
              }}
            >
              <Typography>
                <b
                  style={{
                    color:
                      outgoingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.ToBeClassified.id
                        ? theme.palette.primary.main
                        : outgoingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.Classified.id
                        ? theme.palette.success.light
                        : theme.palette.secondary.main,
                  }}
                >
                  {`${t(`mail_classification_statuses.${outgoingMail.classificationStatus?.name}`)}`}
                </b>
              </Typography>
              {outgoingMail.classificationStatus?.id === MAIL_CLASSIFICATION_STATUS.Classified.id && (
                <>
                  <Typography variant="body2">{outgoingMail.processedDocument?.legalEntity?.code}</Typography>
                  <Typography variant="body2">{outgoingMail.processedDocument?.type?.name}</Typography>
                  <Typography variant="body2">
                    {outgoingMail.document?.type?.dateInsteadPeriod && outgoingMail.document?.documentDate ? (
                      <>{intlFormatDateShort(outgoingMail.document.documentDate)}</>
                    ) : (
                      <>{getItemPeriodValue(outgoingMail.document?.period ?? null)}</>
                    )}
                  </Typography>
                </>
              )}
            </StatusContainer>
          </BlockStatuses>
          <BlockEdit flex={0.1}>
            <OutgoingMailActions
              outgoingMail={outgoingMail}
              handleNotifyParent={handleNotifyParent}
              canUpload={canUpload}
              canClassify={canClassify}
              canRead={canRead}
              canProcess={canProcess}
            />
          </BlockEdit>
        </StyledCard>
      </CardWrapper>

      {fileToView && (
        <DocumentViewerContainer
          open={!!fileToView}
          fullWidth
        >
          <DialogTitle
            title=""
            handleClose={() => {
              setFileToView(null);
            }}
          />
          <Grid style={{ width: "90vw" }}>
            <DocumentViewer
              fileDto={fileToView}
              bypasAllowDownload
              withFooter
              {...(fileToView.id === outgoingMail.processedDocument?.files[0].id &&
              !!outgoingMail.processedDocument.companyAccount
                ? { isGenericDocument: false }
                : { isGenericDocument: true })}
            />
          </Grid>
        </DocumentViewerContainer>
      )}
    </>
  );
};

export default OutgoingMailCard;
